import { useAppDispatch } from 'hooks/redux';
import React from 'react';

import type { ScheduledTransactionType } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { tracker } from 'app-components/utils/core';

import InfoIcon from '@mui/icons-material/InfoRounded';
import LinkIcon from '@mui/icons-material/LinkRounded';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import InitIcon from 'assets/illustrations/BillConnect.svg';
import Content from 'assets/illustrations/BillConnectIntro.svg';
// import HappyStar from 'assets/illustrations/Happy-Star.svg';
import Spaced from 'assets/illustrations/Spaced.svg';

import { doBillPresentment } from 'components/Dialogs/BillPresentment/actions';

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    textAlign: 'center',
    flexDirection: 'column',
    padding: '16px 0',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 544,
    gap: 16,
  },
  icon: {
    width: 168,
    height: 168,
  },
  info: {
    margin: 8,
    marginBottom: 32,
    backgroundColor: theme.palette.info.light,
    borderRadius: 8,
    padding: 8,
  },
  infoIcon: {
    marginRight: 8,
    color: theme.palette.info.main,
  },
}));

interface HoldingScreenProps {
  imgSrc?: string;
  imgAlt?: string;
  title?: string;
  subtitle?: string;
  info?: string;
  button?: any;
}

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Main Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###

const HoldingScreen: React.FC<HoldingScreenProps> = ({ imgSrc, imgAlt, title, subtitle, info, button }) => {
  const { classes } = useStyles();

  return (
    <>
      <div className={classes.root}>
        <img src={imgSrc} className={classes.icon} alt={imgAlt} />
        <div className={classes.column}>
          <Typography variant='headlineRegular'>{title}</Typography>
          <Typography variant='bodyRegular'>{subtitle}</Typography>
          {info ? (
            <Typography variant='paragraphRegular' className={classes.info}>
              <InfoIcon fontSize='small' className={classes.infoIcon} />
              {info}
            </Typography>
          ) : null}
          {button}
        </div>
      </div>
    </>
  );
};

export default HoldingScreen;

// export const CompleteView = () => (
//   <HoldingScreen imgSrc={HappyStar} imgAlt='Happy Star' title='Woo-hoo!' subtitle='Your bills are now connected.' />
// );

export const PendingView = () => (
  <HoldingScreen
    imgSrc={Spaced}
    imgAlt='Bill-Linked'
    title='All set!'
    subtitle='We’re verifying your account details and will let you know once your biller is connected.'
    info='It may take 1-2 days for your biller to connect.'
  />
);

export const IntroView = () => (
  <HoldingScreen
    imgSrc={Content}
    imgAlt='Lightbulb-Calendar'
    title='Connecting your bills'
    subtitle='Find your biller and enter your account details to connect. Simplifi will update your Reminders with your latest bill details for accurate cash flow projection!'
  />
);

export const InitView = (model: ScheduledTransactionType) => {
  const dispatch = useAppDispatch();

  return (
    <HoldingScreen
      imgSrc={InitIcon}
      imgAlt='Bill-Link'
      title='View your bills in Simplifi!'
      subtitle='Connect your biller to automatically update Reminders with your latest bill details—great for cash flow projection!'
      button={
        <Button
          variant='contained'
          id='BPS-connect'
          onClick={() => {
            dispatch(doBillPresentment(model, 'edit series'));
            tracker.track(tracker.events.addBillerStart, { initiator: location });
          }}
        >
          <LinkIcon style={{ marginRight: 4 }} /> Connect biller
        </Button>
      }
    />
  );
};
